import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import JoditEditor from "jodit-react";
import { ProtectAPi } from '../../../api/ProtectAPi';
import swal from 'sweetalert';
import BackButton from '../../tools/BackButton';
import { v4 as uuidv4 } from 'uuid';
import AdminQuickNav from '../landingPage/AdminQuickNav';

/**
 * SizeMultiSelect - chip-style multi-select component.
 * options: array of numbers/strings
 * value: array of selected strings
 * onChange: function(nextArray)
 */
const SizeMultiSelect = ({ options, value = [], onChange, placeholder = 'Select sizes' }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleSize = (s) => {
    const sStr = String(s);
    const exists = value.includes(sStr);
    const next = exists ? value.filter(v => v !== sStr) : [...value, sStr];
    onChange(next);
  };

  const removeChip = (s) => {
    onChange(value.filter(v => v !== String(s)));
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div
        role="button"
        onClick={() => setOpen(o => !o)}
        className="form-control"
        style={{
          minHeight: 44,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          cursor: 'text',
          padding: '6px 8px'
        }}
      >
        {value.length === 0 ? (
          <div style={{ color: '#6c757d' }}>{placeholder}</div>
        ) : (
          value.map(v => (
            <div key={v} style={{
              display: 'inline-flex',
              alignItems: 'center',
              background: '#f1f3f5',
              border: '1px solid #e2e6ea',
              padding: '4px 8px',
              borderRadius: 6,
              marginRight: 6,
            }}>
              <span style={{ marginRight: 6 }}>{v}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeChip(v); }}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  cursor: 'pointer',
                  color: '#333',
                  fontWeight: 700,
                  lineHeight: 1
                }}
                aria-label={`Remove ${v}`}
              >×</button>
            </div>
          ))
        )}

        <div style={{ marginLeft: 'auto', opacity: 0.7 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d={open ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} stroke="#444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </div>
      </div>

      {open && (
        <div style={{
          position: 'absolute',
          zIndex: 999,
          top: 'calc(100% + 6px)',
          left: 0,
          width: '100%',
          maxHeight: 220,
          overflowY: 'auto',
          background: '#fff',
          border: '1px solid rgba(0,0,0,0.1)',
          boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
          borderRadius: 6,
          padding: 8
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {options.map(opt => {
              const selected = value.includes(String(opt));
              return (
                <button
                  type="button"
                  key={opt}
                  onClick={() => toggleSize(opt)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: selected ? '1px solid #3b82f6' : '1px solid #d1d5db',
                    background: selected ? '#eff6ff' : '#fff',
                    cursor: 'pointer'
                  }}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const LandingProductAdd = () => {
  const { http } = useMemo(() => ProtectAPi(), []);
  const editor = useRef(null);
  const navigate = useNavigate();

  const [loader, setLoader] = useState('off');

  // Product fields
  const [productName, setProductName] = useState('');
  const [articleNumber, setArticleNumber] = useState('');
  const [shortDescription, setShortDescription] = useState('');
  const [productSlug, setProductSlug] = useState('');
  const [layoutDescription, setLayoutDescription] = useState('');
  const [price, setPrice] = useState('');
  const [priority, setPriority] = useState('');
  const [status, setStatus] = useState(1);

  // careguide & sizeguide are files now (optional)
  const [careguideFile, setCareguideFile] = useState(null);
  const [sizeguideFile, setSizeguideFile] = useState(null);

  // sizes (multi-select)
  const [selectedSizes, setSelectedSizes] = useState([]);

  // general images (file, preview, alt, chosen color id)
  const [layoutImages, setLayoutImages] = useState([]); // { file, preview, alt, colorId }
  const [layoutImageErrors, setLayoutImageErrors] = useState([]);

  // colors loaded from API for selection
  const [colorOptions, setColorOptions] = useState([]);

  // color rows admin can add to pick a color + upload color-specific images
  const [colors, setColors] = useState([]); // {tmp_key, colorId, colorCode, files: [{file, preview}]}

  // validation errors from frontend/back
  const [errors, setErrors] = useState({});

  const sizeOptions = [
    'S',
    'M',
    'L',
    'XL',
    ...Array.from({ length: 12 }, (_, i) => 34 + i), // 34..45
  ];



  // load color options
useEffect(() => {
  http.get('/get-landing-product-color')
    .then(res => {
      const payload = res.data;
      const list = Array.isArray(payload) ? payload : (payload && Array.isArray(payload.data) ? payload.data : []);
      setColorOptions(list);
    })
    .catch(err => console.error('Color fetch error', err));
}, []);   // ✔️ only once on mount


  // ---------- general images handlers ----------
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      alt: '',
      colorId: ''
    }));
    setLayoutImages(prev => [...prev, ...newImages]);
    setLayoutImageErrors([]);
  };

  const handleAltChange = (index, value) => {
    setLayoutImages(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], alt: value };
      return copy;
    });
  };

  const handleImageColorChange = (index, colorId) => {
    setLayoutImages(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], colorId };
      return copy;
    });
  };

  const handleImageRemove = (index) => {
    setLayoutImages(prev => {
      const copy = [...prev];
      if (copy[index] && copy[index].preview) URL.revokeObjectURL(copy[index].preview);
      copy.splice(index, 1);
      return copy;
    });
  };

  // ---------- careguide & sizeguide file handlers ----------
  const handleCareguideFile = (e) => {
    const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setCareguideFile(f);
  };
  const handleSizeguideFile = (e) => {
    const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setSizeguideFile(f);
  };

  // ---------- color-rows handlers ----------
  const addColor = () => {
    const tmp = 'c_' + uuidv4().slice(0, 8);
    setColors(prev => [...prev, { tmp_key: tmp, colorId: '', colorCode: '', files: [] }]);
  };

  const removeColor = (idx) => {
    setColors(prev => {
      const copy = [...prev];
      (copy[idx].files || []).forEach(f => f.preview && URL.revokeObjectURL(f.preview));
      copy.splice(idx, 1);
      return copy;
    });
  };

  const updateColorSelect = (idx, colorId) => {
    const opt = colorOptions.find(co => String(co.id) === String(colorId)) || {};
    const code = opt.color_code || opt.colorCode || opt.color || opt.code || opt.color_name_code || '';
    setColors(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], colorId, colorCode: code || '' };
      return copy;
    });
  };

  const handleColorFilesChange = (idx, e) => {
    const files = Array.from(e.target.files || []);
    const newFiles = files.map(file => ({ file, preview: URL.createObjectURL(file) }));
    setColors(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], files: [...(copy[idx].files || []), ...newFiles] };
      return copy;
    });
  };

  const removeColorFile = (colorIdx, fileIdx) => {
    setColors(prev => {
      const copy = [...prev];
      const f = copy[colorIdx].files || [];
      if (f[fileIdx]) {
        f[fileIdx].preview && URL.revokeObjectURL(f[fileIdx].preview);
        f.splice(fileIdx, 1);
      }
      copy[colorIdx].files = f;
      return copy;
    });
  };

  // ---------- frontend validation ----------
  const validateFrontend = () => {
    const e = {};
    if (!productName || !productName.trim()) e.product_name = 'Product name is required.';
    if (!articleNumber || !articleNumber.trim()) e.article_number = 'Article number is required.';
    if (!price && price !== 0) e.price = 'Price is required.';
    if (!selectedSizes || selectedSizes.length === 0) e.size = 'Select at least one size.';
    if (!layoutImages || layoutImages.length === 0) e.images = 'Upload at least one product image.';
    return e;
  };

  // ---------- submit ----------
  const submitForm = async () => {
    if (loader === 'on') return; // prevent double submit
    setLoader('on');
    setErrors({});
    setLayoutImageErrors([]);

    const frontErrors = validateFrontend();
    if (Object.keys(frontErrors).length > 0) {
      setErrors(frontErrors);
      setLoader('off');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const formData = new FormData();
    formData.append('product_name', productName);
    formData.append('article_number', articleNumber);
    formData.append('short_description', shortDescription);
    formData.append('slug', productSlug); // optional
    formData.append('description', layoutDescription); // optional
    formData.append('price', price);
    if (priority !== '') formData.append('priority', priority); // optional
    formData.append('status', status);

    if (careguideFile) formData.append('careguide', careguideFile); // optional file
    if (sizeguideFile) formData.append('sizeguide', sizeguideFile); // optional file

    // sizes[] (multiple)
    selectedSizes.forEach(s => formData.append('size[]', s));

    // general images: images[] + alt_texts[index] + image_color_ids[index]
    layoutImages.forEach((img, index) => {
      formData.append('images[]', img.file);
      formData.append(`alt_texts[${index}]`, img.alt || '');
      formData.append(`image_color_ids[${index}]`, img.colorId || '');
    });

    // colors JSON (tmp_key + color_id)
    const colorsPayload = colors.map(c => ({ tmp_key: c.tmp_key, color_id: c.colorId, name: null, code: c.colorCode || null }));
    formData.append('colors', JSON.stringify(colorsPayload));


    // 🔥 FIXED color images (FLAT ARRAY – Laravel friendly)
    colors.forEach(c => {
      if (c.files && c.files.length && c.colorId) {
        c.files.forEach(f => {
          formData.append('color_images[]', f.file);
          formData.append('color_image_color_ids[]', c.colorId);
        });
      }
    });



    try {
      const res = await http.post('/store-landing-product', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setLoader('off');

      if (res.data && res.data.status === '422') {
        // backend validation style you used earlier
        const vErrors = res.data.errors || res.data.error || {};
        setErrors(vErrors);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (res.status === 201 || (res.data && (res.data.success || res.data.product_id))) {
        swal('Success', res.data.message || res.data.msg || 'Product created', 'success');
        navigate('/admin/landing-page-products');
      } else {
        swal('Info', res.data.message || 'Unexpected response', 'info');
      }
    } catch (error) {
      setLoader('off');
      if (error.response && error.response.data) {
        const resp = error.response.data;
        if (resp.errors) setErrors(resp.errors);
        if (resp.error) setErrors(resp.error);
      } else {
        console.error(error);
        swal('Error', 'Network or server error', 'error');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const editorConfig = useMemo(() => ({
    readonly: false,
    placeholder: 'Start typing...',
    height: 300,
    uploader: { insertImageAsBase64URI: true }
  }), []);

  // small helper to render label with required star
  const Label = ({ children, required }) => (
    <label>{children} {required && <span style={{ color: 'red' }}>*</span>}</label>
  );

  return (
    <div className="main-content" style={{ marginTop: -20 }}>
      <section className="section">
        <div className="section-body">
          <div className="row">
            <div className="col-12">
              <AdminQuickNav active="products" />

              <div className="card card-primary">
                <div className="card-body">
                  <div className="d-flex justify-content-between mb-3">
                    <h5>Add Product</h5>
                    <BackButton />
                  </div>

              

                  {/* General images */}
                  <div className="form-group mt-4">
                    <Label required>Multiple Product Images</Label>
                    <input type="file" className="form-control" multiple onChange={handleImageChange} />
                    {errors.images && <div className="text-danger mt-1">{errors.images}</div>}
                    {layoutImageErrors.length > 0 && layoutImageErrors.map((err, i) => (
                      <span key={i} className="text-danger d-block">{err}</span>
                    ))}
                  </div>

                  {/* preview general images with alt + color select */}
                  {layoutImages.length > 0 && (
                    <div className="row mt-3">
                      {layoutImages.map((img, index) => (
                        <div className="col-md-3 mb-4" key={index}>
                          <div className="border p-2 rounded shadow-sm">
                            <img src={img.preview} alt="Preview" className="img-fluid mb-2" style={{ height: '150px', width: '100%', objectFit: 'cover' }} />
                            <input
                              type="text"
                              className="form-control mb-2"
                              placeholder="Alt text (e.g. front view)"
                              value={img.alt}
                              onChange={e => handleAltChange(index, e.target.value)}
                            />
                            <select className="form-control mb-2" value={img.colorId || ''} onChange={e => handleImageColorChange(index, e.target.value)}>
                              <option value="">Select Color (optional)</option>
                              {colorOptions.map(c => (
                                <option key={c.id} value={c.id}>
                                  {c.color_name || c.name || String(c.id)}
                                </option>
                              ))}
                            </select>
                            <button type="button" className="btn btn-danger btn-md w-100 mt-2" onClick={() => handleImageRemove(index)}>
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

               
                  <div className="form-row">
                  
                    <div className="form-group col-md-6">
                      <Label required>Product Name</Label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter product name"
                        value={productName}
                        onChange={e => setProductName(e.target.value)}
                      />
                      {errors.product_name && <span className="text-danger">{errors.product_name}</span>}
                    </div>

                    <div className="form-group col-md-6">
                      <Label required>Article Number</Label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Article Number"
                        value={articleNumber}
                        onChange={e => setArticleNumber(e.target.value)}
                      />
                      {errors.article_number && <span className="text-danger">{errors.article_number}</span>}
                    </div>

                    <div className="form-group col-md-12">
                      <Label>Short Description</Label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Short description (one-liner)"
                        value={shortDescription}
                        onChange={e => setShortDescription(e.target.value)}
                      />
                      {errors.short_description && <span className="text-danger">{errors.short_description}</span>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group col-md-3">
                      <Label>Slug (optional)</Label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Slug"
                        value={productSlug}
                        onChange={e => setProductSlug(e.target.value)}
                      />
                      {errors.slug && <span className="text-danger">{errors.slug}</span>}
                    </div>
                    <div className="form-group col-md-3">
                      <Label required>Price</Label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        placeholder="Enter Price in BDT"
                        value={price}
                        onChange={e => setPrice(e.target.value)}
                      />
                      {errors.price && <span className="text-danger">{errors.price}</span>}
                    </div>
                    <div className="form-group col-md-3">
                      <Label>Priority (optional)</Label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter Priority (optional)"
                        value={priority}
                        onChange={e => setPriority(e.target.value)}
                      />
                      {errors.priority && <span className="text-danger">{errors.priority}</span>}
                      
                      </div>
                        <div className="form-group col-md-3">
                          <Label required>Status</Label>
                          <select
                            name="status"
                            className="form-control"
                            value={status}
                            onChange={e => setStatus(e.target.value)}
                          >
                            <option value="1">Active</option>
                            <option value="2">Deactive</option>
                          </select>

                          {errors.status && (
                            <span className="text-danger">{errors.status}</span>
                          )}
                        </div>

                  </div>

                  <div className="form-group">
                    <Label required>Sizes</Label>
                    <SizeMultiSelect
                      options={sizeOptions}
                      value={selectedSizes}
                      onChange={(next) => setSelectedSizes(next)}
                      placeholder="Select sizes (click to open)"
                    />
                    {errors.size && <div className="text-danger">{errors.size}</div>}
                  </div>

                  {/* careguide & sizeguide files (optional) */}
                  <div className="form-row">
                    <div className="form-group col-md-6">
                      <Label>Care Guide (file) <small className="text-muted">(optional)</small></Label>
                      <input type="file" className="form-control" onChange={handleCareguideFile} />
                      {errors.careguide && <div className="text-danger">{errors.careguide}</div>}
                    </div>
                    <div className="form-group col-md-6">
                      <Label>Size Guide (file) <small className="text-muted">(optional)</small></Label>
                      <input type="file" className="form-control" onChange={handleSizeguideFile} />
                      {errors.sizeguide && <div className="text-danger">{errors.sizeguide}</div>}
                    </div>
                  </div>

                  {/* colors table */}
                  <div className="mb-3 border rounded p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6>Select Color & Color Image</h6>
                      <button type="button" className="btn btn-md mb-2 btn-primary" onClick={addColor}>Add Color Row</button>
                    </div>

                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th style={{width: '35%'}}>Select Color</th>
                            <th style={{width: '20%'}}>Color Code</th>
                            <th style={{width: '35%'}}>Upload Color Images</th>
                            <th style={{width: '10%'}}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {colors.length === 0 && (
                            <tr>
                              <td colSpan={4} className="text-center">No color rows. Click "Add Color Row" to add.</td>
                            </tr>
                          )}
                          {colors.map((c, idx) => (
                            <tr key={c.tmp_key}>
                              <td>
                                <select className="form-control" value={c.colorId || ''} onChange={e => updateColorSelect(idx, e.target.value)}>
                                  <option value="">Choose Color</option>
                                  {colorOptions.map(opt => (
                                    <option key={opt.id} value={opt.id}>
                                      {opt.color_name || opt.name || `#${opt.id}`}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                <input className="form-control" value={c.colorCode || ''} readOnly placeholder="Auto-filled code" />
                              </td>
                              <td>
                                <input type="file" className="form-control" multiple onChange={e => handleColorFilesChange(idx, e)} />
                               
                              </td>
                              <td className="text-center">
                                <button type="button" className="btn btn-sm btn-danger" onClick={() => removeColor(idx)}>Remove</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* description (optional) */}
                  <div className="form-group">
                    <Label>Description <small className="text-muted">(optional)</small></Label>
                    <JoditEditor
                      ref={editor}
                      value={layoutDescription}
                      config={editorConfig}
                      tabIndex={1}
                      onBlur={newContent => setLayoutDescription(newContent)}
                      onChange={() => { }}
                    />
                    {errors.description && <span className="text-danger">{errors.description}</span>}
                  </div>


                  

                  {/* submit */}
                  <div className="mb-2 mt-4">
                    {loader === 'off' ? (
                      <button className="btn btn-primary btn-lg" onClick={submitForm}>Create Product</button>
                    ) : (
                      <button className="btn btn-primary btn-lg" disabled>
                        <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                        Saving...
                      </button>
                    )}
                  </div>

                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingProductAdd;
